import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import clsx from "clsx";
import { useSelector } from "react-redux";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import useJStyles from "$hooks/useJStyles";
import { Layout, Paper } from "$components";
import { CompanyServices, EntrepotService } from "$services";

import {
  DatableWithoutContext,
  DataTableProvider,
  EnumDataRowType,
  EnumFilterTypes,
} from "$components/DataTable";
import { getProductStock } from "$services/Products.service";
import { useQueryParameters } from "$hooks/useQueryParameters";
import { Loader } from "$components/Layout/Loader";
import { StockCompanySelect } from "../StockCompanySelect";
import useAuth from "$hooks/useAuth";
import { useStyles } from "../Stock.styles";
import { CompanyStockInfo } from "./CompanyStockInfo";
import { appState } from "$reducers";
import { ModalPatch } from "./ModalPatch";
import useCompanyStock from "$hooks/Stock/useCompanyStock";
import { CacheInvalidationAction } from "$utils/cache";

interface RouteParams {
  idCompany?: string;
}

const useFetchWarehouseInfo = ({ companyId, includeRelatedFiles }) => {
  const [warehouseTotal, setWarehouse] = useState(0);

  // TODO add the contributor too
  const { data: warehouses, isLoading: isLoadingWarehouse } = useQuery(
    [
      CacheInvalidationAction.GET_ENTREPOTS,
      { $withContributorOf: companyId, includeRelatedFiles },
    ],
    () =>
      EntrepotService.getEntrepots({
        $withContributorOf: companyId,
        includeRelatedFiles,
      }),
    {
      enabled: !!companyId,
      cacheTime: 5 * 1000 * 60,
    }
  ) as any;

  useEffect(() => {
    if (warehouses?.data?.length) {
      setWarehouse(warehouses.data.length);
    }
  }, [warehouses]);

  return {
    warehouses: warehouses?.data ?? [],
    isLoadingWarehouse,
    warehouseTotal,
  };
};

const CompanyStock = () => {
  const { jclasses } = useJStyles();
  const params = useQueryParameters();
  const { filter: companySidebarFilter } = useSelector(appState);
  const { idCompany: companyId } = useParams<RouteParams>();
  const contributorId = params.get("contributor");
  const { isAdmin } = useAuth();
  const classes = useStyles();
  const currentCompany = companyId ?? companySidebarFilter?._id;

  const { warehouses, isLoadingWarehouse, warehouseTotal } =
    useFetchWarehouseInfo({
      companyId: companySidebarFilter?._id ?? companyId,
      includeRelatedFiles: true,
    });

  const { data: company, isLoading } = useQuery(
    [CacheInvalidationAction.GET_COMPANY_BY_ID, currentCompany],
    () => CompanyServices.getCompanyById(currentCompany),
    {
      enabled: !!currentCompany,
      staleTime: 1000 * 60,
    }
  );

  const { data: contributor } = useQuery(
    [CacheInvalidationAction.GET_COMPANY_BY_ID, contributorId],
    () => CompanyServices.getCompanyById(contributorId),
    {
      enabled: !!contributorId,
      staleTime: 1000 * 60,
    }
  ) as any;

  const companyStockService = useCompanyStock(companyId, contributor);

  const externalState = useMemo(() => {
    const returnState = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of params.entries()) {
      returnState[key] = value;
    }
    const { page, rowsPerPage, ...filters } = returnState as any;

    return {
      filters: {
        ...filters,
        company: currentCompany,
        isActive: true,
      },
      pagination: { page, rowsPerPage },
    } as any;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, companyId, currentCompany]);

  const headWarehouse = useCallback(
    (warehouse) => {
      const importedFile = warehouse.importedFiles
        ?.filter((w) => w.company === currentCompany)
        .sort(
          (
            a: { date: string | number | Date },
            b: { date: string | number | Date }
          ) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
      const dateNow = new Date().setHours(0, 0, 0, 0);
      const dateImport = new Date(importedFile?.[0]?.date).setHours(0, 0, 0, 0);

      // FINISH THIS FOR NB UPDATE WAREHOUSE

      return {
        id: `warehouse${warehouse._id}`,
        exportLabel: `qt ${warehouse.name}*`,
        label: (
          <div
            onClick={() =>
              companyStockService.handleSelectEntrepot(warehouse._id)
            }
            className={clsx(
              classes.entrepotHead,
              isAdmin() && "admin",
              dateNow === dateImport && "actual"
            )}
          >
            qt {warehouse.name}
            <Upload2LineIcon />
          </div>
        ),
        cell: {
          value: ({ stock }) =>
            stock.stocks?.find((s) => s?.entrepot?._id === warehouse._id)
              ?.quantityLastImport ?? 0,
        },
      };
    },
    [companyStockService, classes.entrepotHead, currentCompany, isAdmin]
  );

  // TODO faire le formatage
  const headerData = useMemo(
    () => [
      {
        id: "reference",
        exportLabel: "ref principale*",
        filter: {
          type: EnumFilterTypes.INPUT,
          label: "Référence",
          placeholder: "Par nom ou référence",
          value: params.get("reference"),
        },
        mminWidth: 260,
        maxWidth: 300,
        cell: {
          type: EnumDataRowType.EmptyLink,
          value: ({ reference }) => reference,
        },
      },

      {
        id: "designation",
        exportLabel: "désignation*",
        filter: {
          type: EnumFilterTypes.INPUT,
          label: "Désignation",
          placeholder: "Par désignation",
          value: params.get("designation"),
        },
        cell: { value: ({ designation }) => designation },
      },
      {
        id: "unit",
        exportLabel: "unité de mesure*",
        filter: {
          type: EnumFilterTypes.INPUT,
          label: "Unité",
          placeholder: "",
          value: params.get("unit"),
        },
        cell: { value: ({ unit }) => unit },
      },
      {
        id: "quantity",
        filter: {
          type: EnumFilterTypes.INPUT,
          label: "Qté totale",
          placeholder: "",
          value: params.get("quantity"),
        },
        mminWidth: 260,
        maxWidth: 300,
        noExport: true,
        cell: { value: ({ stock }) => stock.quantityLastImport ?? 0 },
      },
      {
        id: "edbReservedQuantity",
        exportLabel: "qté réservée",
        filter: {
          type: EnumFilterTypes.INPUT,
          label: "Qté réservée",
          placeholder: "",
          value: params.get("edbReservedQuantity"),
        },
        noExport: true,
        cell: { value: ({ stock }) => stock.edbReservedQuantity ?? 0 },
      },
      ...(warehouses && !isLoadingWarehouse
        ? warehouses.map((w) => ({ ...headWarehouse(w) }))
        : []),
    ],
    [headWarehouse, isLoadingWarehouse, params, warehouses]
  );

  // ATTENTION LE Total d'unité en stock ne peut plus être calculer avec la pagination
  return (
    <Box className={jclasses.jWrapper}>
      <Layout
        title={`Liste des stocks - ${company?.name ?? ""}`}
        renderHead={() => <StockCompanySelect />}
        contentClassName={jclasses.jLayoutCustom}
      >
        {isLoading && <Loader />}
        {!isLoading && (
          <Paper className={jclasses.jPaper}>
            <DataTableProvider externalState={externalState}>
              <CompanyStockInfo
                warehouseTotal={warehouseTotal}
                company={company}
                companyId={currentCompany}
                contributor={contributor}
              />
              <DatableWithoutContext
                rowsPerPage={50}
                thead={headerData}
                queryFunction={getProductStock}
                queryFunctionName={CacheInvalidationAction.GET_PRODUCT_STOCK}
                data-testid={`productStock_${currentCompany}`}
              />
            </DataTableProvider>
            <ModalPatch {...companyStockService} companyId={companyId} />
          </Paper>
        )}
      </Layout>
    </Box>
  );
};

export default CompanyStock;
